import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  Building2, Upload, Trash2, Save, ImageIcon, AlertTriangle, Mail, X, KeyRound,
  SlidersHorizontal, CreditCard, Palette, Sparkles, ExternalLink, CheckCircle2, Loader2, Receipt, Bell, PoundSterling,
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import ProUpsell from '@/components/ProUpsell';
import Seo from '@/components/Seo';
import PriceListSection from '@/components/PriceListSection';
import MonthlyReportSection from '@/components/MonthlyReportSection';
import { RATE_FIELDS, DEFAULT_RATES } from '@/lib/planMaterials';
import { knockoutWhite } from '@/lib/imageUtils';

const BRAND_PRESETS = ['#FF5A1F', '#0F172A', '#2563EB', '#059669', '#DC2626', '#7C3AED', '#DB2777', '#EA580C', '#0891B2', '#CA8A04'];

const DEFAULT_REMINDER_MSG = 'Hi {customer}, this is a friendly reminder that invoice {invoice_number} for {amount} was due on {due_date} and is still outstanding. You can view it and pay using the details here: {link}. Thanks, {business}.';

const CHECKER_BG = {
  backgroundImage: 'linear-gradient(45deg,#e2e8f0 25%,transparent 25%),linear-gradient(-45deg,#e2e8f0 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e2e8f0 75%),linear-gradient(-45deg,transparent 75%,#e2e8f0 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
};

const TABS = [
  { id: 'business', label: 'Business', icon: Building2, pro: true },
  { id: 'branding', label: 'Branding', icon: Palette, pro: true },
  { id: 'rates', label: 'Material Rates', icon: SlidersHorizontal, pro: true },
  { id: 'prices', label: 'Price List', icon: PoundSterling, pro: true },
  { id: 'invoices', label: 'Invoices', icon: Receipt, pro: true },
  { id: 'notifications', label: 'Notifications', icon: Bell, pro: false },
  { id: 'security', label: 'Security', icon: KeyRound, pro: false },
  { id: 'subscription', label: 'Subscription', icon: CreditCard, pro: false },
  { id: 'account', label: 'Account', icon: AlertTriangle, pro: false },
];

export default function SettingsPage() {
  const { user, refresh, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'business');

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (!user) return null;
  const isProOrAdmin = user.is_pro || user.role === 'admin';

  const selectTab = (id) => {
    setTab(id);
    setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set('tab', id); return p; }, { replace: true });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="Settings" noindex />
      <Toaster richColors position="top-right" />

      <header className="mb-8">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">Settings</p>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">Settings</h1>
        <p className="text-slate-600 mt-2">Signed in as <strong className="text-slate-900">{user.email}</strong>. Manage your business profile, branding, estimating rates, security and subscription.</p>
      </header>

      {/* Tab bar */}
      <nav data-testid="settings-tabs" className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 mb-8 border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`settings-tab-${t.id}`}
              onClick={() => selectTab(t.id)}
              className={`shrink-0 inline-flex items-center gap-2 px-4 py-2.5 font-display font-bold uppercase text-xs tracking-wider border transition-colors ${
                active
                  ? 'bg-slate-900 text-white border-slate-800'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-brand hover:text-brand'
              }`}
            >
              <Icon size={15} className={active ? 'text-brand' : ''} /> {t.label}
            </button>
          );
        })}
      </nav>

      <div data-testid={`settings-panel-${tab}`}>
        {tab === 'business' && (isProOrAdmin ? <BusinessSection user={user} refresh={refresh} /> : <LockedSection feature="Business Profile" />)}
        {tab === 'branding' && (isProOrAdmin ? <BrandingSection user={user} refresh={refresh} /> : <LockedSection feature="Logo & Branding" />)}
        {tab === 'rates' && (isProOrAdmin ? <RatesSection /> : <LockedSection feature="Material Coverage Rates" />)}
        {tab === 'prices' && (isProOrAdmin ? <PriceListSection user={user} refresh={refresh} /> : <LockedSection feature="Price List" />)}
        {tab === 'invoices' && (isProOrAdmin ? <InvoiceSettingsSection user={user} refresh={refresh} /> : <LockedSection feature="Invoice Settings" />)}
        {tab === 'notifications' && <MonthlyReportSection user={user} refresh={refresh} />}
        {tab === 'security' && <ChangePassword />}
        {tab === 'subscription' && <SubscriptionSection user={user} />}
        {tab === 'account' && <AccountSection user={user} logout={logout} />}
      </div>

      <p className="mt-10 text-sm text-slate-500">
        <Link to="/dashboard" className="underline">← Back to dashboard</Link>
      </p>
    </div>
  );
}

function LockedSection({ feature }) {
  return <ProUpsell feature={feature} testIdSuffix="settings" tier="pro" />;
}

function SectionHead({ icon: Icon, title, sub }) {
  return (
    <div className="mb-1">
      <div className="flex items-center gap-2">
        <Icon className="text-brand" size={22} />
        <h2 className="font-display font-bold text-xl uppercase tracking-tight">{title}</h2>
      </div>
      {sub && <p className="text-sm text-slate-600 mt-1">{sub}</p>}
    </div>
  );
}

/* ------------------------------- Business ------------------------------- */
function BusinessSection({ user, refresh }) {
  const [form, setForm] = useState({
    business_name: '', business_address: '', business_phone: '', business_email: '', business_vat_number: '', payment_details: '', default_terms: '', notify_repeat_views: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm({
      business_name: user.business_name || '',
      business_address: user.business_address || '',
      business_phone: user.business_phone || '',
      business_email: user.business_email || user.email || '',
      business_vat_number: user.business_vat_number || '',
      payment_details: user.payment_details || '',
      default_terms: user.default_terms || '',
      notify_repeat_views: user.notify_repeat_views !== false,
    });
  }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch('/me/business', form);
      if (refresh) await refresh();
      toast.success('Business details saved');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="tm-card p-6 space-y-4">
      <SectionHead icon={Building2} title="Business Details" sub="These appear on every invoice, PDF and public quote you send." />
      <div>
        <label className="tm-label">Business / Trading name</label>
        <input data-testid="settings-business-name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="tm-input" placeholder="Dave the Builder Ltd" />
      </div>
      <div>
        <label className="tm-label">Address</label>
        <textarea data-testid="settings-business-address" value={form.business_address} onChange={(e) => setForm({ ...form, business_address: e.target.value })} className="tm-input" rows={3} placeholder="12 Trade Way&#10;Anytown&#10;AB1 2CD" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="tm-label">Phone</label>
          <input data-testid="settings-business-phone" value={form.business_phone} onChange={(e) => setForm({ ...form, business_phone: e.target.value })} className="tm-input" placeholder="07123 456789" />
        </div>
        <div>
          <label className="tm-label">Email</label>
          <input data-testid="settings-business-email" type="email" value={form.business_email} onChange={(e) => setForm({ ...form, business_email: e.target.value })} className="tm-input" />
        </div>
      </div>
      <div>
        <label className="tm-label">VAT number (if registered)</label>
        <input data-testid="settings-business-vat" value={form.business_vat_number} onChange={(e) => setForm({ ...form, business_vat_number: e.target.value })} className="tm-input" placeholder="GB 123 4567 89" />
      </div>
      <div>
        <label className="tm-label">Default payment details (shown on invoices)</label>
        <textarea data-testid="settings-payment-details" value={form.payment_details} onChange={(e) => setForm({ ...form, payment_details: e.target.value })} className="tm-input" rows={3} placeholder={'e.g. Bank transfer\nSort code 00-00-00\nAccount 12345678\nRef: your invoice number'} />
      </div>
      <div>
        <label className="tm-label">Default Terms &amp; Conditions (pre-fills every new quote)</label>
        <textarea data-testid="settings-default-terms" value={form.default_terms} onChange={(e) => setForm({ ...form, default_terms: e.target.value })} className="tm-input" rows={5} placeholder={'e.g.\n• 50% deposit required before work begins.\n• Prices exclude unforeseen remedial works.\n• 12 month guarantee on labour.\n• Access and parking to be provided by the customer.'} />
        <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-1 font-semibold">New quotes start with these terms — you can still edit them per quote</p>
      </div>
      <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="settings-notify-repeat-views-label">
        <input type="checkbox" data-testid="settings-notify-repeat-views" checked={form.notify_repeat_views} onChange={(e) => setForm({ ...form, notify_repeat_views: e.target.checked })} className="mt-1 w-4 h-4" />
        <span><span className="font-semibold">Email me every time a customer re-opens a quote</span><br /><span className="text-slate-500">You&apos;ll always get an email on the first view. Repeat-view emails are sent at most once every 30 minutes per quote.</span></span>
      </label>
      <button data-testid="settings-save" onClick={save} disabled={busy} className="tm-btn">
        <Save size={16} /> {busy ? 'Saving…' : 'Save details'}
      </button>
    </div>
  );
}

/* ------------------------------- Branding ------------------------------- */
function BrandingSection({ user, refresh }) {
  const [logoUrl, setLogoUrl] = useState(null);          // stored/processed logo (saved)
  const [originalLogo, setOriginalLogo] = useState(null); // raw upload, pre-knockout
  const [preview, setPreview] = useState(null);           // what's shown in the box
  const [brandColor, setBrandColor] = useState(user.brand_color || '#FF5A1F');
  const [transparent, setTransparent] = useState(!!user.logo_transparent);
  const [threshold, setThreshold] = useState(user.logo_threshold || 238);
  const [busy, setBusy] = useState(false);
  const [savingColor, setSavingColor] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user.has_logo) {
      api.get('/me/logo').then(({ data }) => {
        setLogoUrl(data.data_url);
        setOriginalLogo(data.original || data.data_url);
      }).catch(() => {});
    }
    setBrandColor(user.brand_color || '#FF5A1F');
    setTransparent(!!user.logo_transparent);
    setThreshold(user.logo_threshold || 238);
  }, [user]);

  // Live preview: reprocess the ORIGINAL whenever the toggle or threshold changes.
  useEffect(() => {
    let cancelled = false;
    const src = originalLogo || logoUrl;
    if (!src) { setPreview(null); return; }
    if (!transparent) { setPreview(src); return; }
    const t = setTimeout(() => {
      knockoutWhite(src, threshold)
        .then((out) => { if (!cancelled) setPreview(out); })
        .catch(() => { if (!cancelled) setPreview(src); });
    }, 120);
    return () => { cancelled = true; clearTimeout(t); };
  }, [originalLogo, logoUrl, transparent, threshold]);

  const persist = async (rawSrc, isTransparent, thr) => {
    setBusy(true);
    try {
      const dataUrl = isTransparent ? await knockoutWhite(rawSrc, thr) : rawSrc;
      await api.post('/me/logo', { data_url: dataUrl, original: rawSrc, transparent: isTransparent, threshold: thr });
      setLogoUrl(dataUrl);
      if (refresh) await refresh();
      toast.success('Logo saved');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Could not save logo');
    } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2 MB'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const raw = reader.result;
      setOriginalLogo(raw);
      await persist(raw, transparent, threshold);
    };
    reader.readAsDataURL(file);
  };

  const toggleTransparent = async (checked) => {
    setTransparent(checked);
    const src = originalLogo || logoUrl;
    if (src) await persist(src, checked, threshold);
    else { try { await api.patch('/me/logo-options', { transparent: checked }); if (refresh) await refresh(); } catch { /* noop */ } }
  };

  const saveThreshold = async () => {
    const src = originalLogo || logoUrl;
    if (src) await persist(src, true, threshold);
  };

  const removeLogo = async () => {
    if (!window.confirm('Remove logo?')) return;
    try {
      await api.delete('/me/logo');
      setLogoUrl(null);
      if (refresh) await refresh();
      toast.success('Logo removed');
    } catch { toast.error('Remove failed'); }
  };

  const saveColor = async () => {
    const hex = (brandColor || '').trim();
    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) { toast.error('Enter a hex colour like #FF5A1F'); return; }
    setSavingColor(true);
    try {
      await api.patch('/me/business', { brand_color: hex.toUpperCase() });
      if (refresh) await refresh();
      toast.success('Brand colour saved');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Save failed');
    } finally { setSavingColor(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="tm-card p-6 space-y-4">
        <SectionHead icon={ImageIcon} title="Logo" sub="Shows on printed invoices and your public quote pages. PNG or JPG, max 2 MB." />
        <div
          className="border border-dashed border-slate-200 p-6 flex items-center justify-center min-h-[200px]"
          style={transparent ? CHECKER_BG : { background: '#f8fafc' }}
        >
          {preview ? (
            <img src={preview} alt="Logo" className="max-h-48 max-w-full object-contain" data-testid="settings-logo-preview" />
          ) : (
            <p className="text-sm text-slate-500 uppercase tracking-widest font-semibold">No logo uploaded</p>
          )}
        </div>

        <label className="flex items-start gap-3 cursor-pointer select-none border border-slate-200 p-3">
          <input data-testid="logo-transparent-toggle" type="checkbox" checked={transparent} onChange={(e) => toggleTransparent(e.target.checked)} disabled={busy} className="w-5 h-5 accent-brand mt-0.5" />
          <span>
            <span className="font-display font-bold uppercase text-sm tracking-wide">Transparent background</span>
            <span className="block text-xs text-slate-600 mt-0.5">Removes the white box so only the logo itself shows. Best with a logo on a plain white background.</span>
          </span>
        </label>

        {transparent && (
          <div className="border border-slate-200 p-3 space-y-2" data-testid="logo-threshold-panel">
            <div className="flex items-center justify-between">
              <label className="tm-label mb-0">White removal strength</label>
              <span className="font-mono text-xs text-slate-600" data-testid="logo-threshold-value">{threshold}</span>
            </div>
            <input
              data-testid="logo-threshold-slider"
              type="range" min={180} max={255} step={1}
              value={threshold}
              onChange={(e) => setThreshold(parseInt(e.target.value))}
              className="w-full accent-brand"
            />
            <div className="flex justify-between text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
              <span>← More aggressive</span>
              <span>Only pure white →</span>
            </div>
            <p className="text-xs text-slate-600">Lower removes light greys too (good for tricky logos); higher removes only near-white. Preview updates live — Save to apply.</p>
            <button data-testid="logo-threshold-save" onClick={saveThreshold} disabled={busy || !originalLogo} className="tm-btn tm-btn-ghost text-sm">
              <Save size={15} /> Save strength
            </button>
          </div>
        )}

        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={onFile} className="hidden" data-testid="settings-logo-input" />
        <div className="flex flex-wrap gap-2">
          <button data-testid="settings-logo-upload" onClick={() => fileRef.current?.click()} disabled={busy} className="tm-btn">
            <Upload size={16} /> {logoUrl ? 'Replace logo' : 'Upload logo'}
          </button>
          {logoUrl && (
            <button data-testid="settings-logo-remove" onClick={removeLogo} className="tm-btn tm-btn-ghost">
              <Trash2 size={16} /> Remove
            </button>
          )}
        </div>
      </div>

      <div className="tm-card p-6 space-y-4">
        <SectionHead icon={Palette} title="Brand Colour" sub="Used as the accent on your public quote pages and PDF totals." />
        <div className="flex flex-wrap gap-2">
          {BRAND_PRESETS.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`brand-preset-${c.replace('#', '')}`}
              onClick={() => setBrandColor(c)}
              aria-label={`Choose ${c}`}
              className={`w-9 h-9 border transition-transform ${brandColor.toUpperCase() === c ? 'border-slate-200 scale-110 shadow-md' : 'border-slate-300 hover:scale-105'}`}
              style={{ background: c }}
            />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <input
            type="color"
            data-testid="brand-color-picker"
            value={/^#[0-9a-fA-F]{6}$/.test(brandColor) ? brandColor : '#FF5A1F'}
            onChange={(e) => setBrandColor(e.target.value)}
            className="w-12 h-11 border border-slate-200 bg-white p-0.5 cursor-pointer"
          />
          <input
            data-testid="brand-color-hex"
            value={brandColor}
            onChange={(e) => setBrandColor(e.target.value)}
            className="tm-input uppercase font-semibold"
            placeholder="#FF5A1F"
            maxLength={7}
          />
        </div>
        <div className="border border-slate-200 p-4">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 font-semibold">Preview</p>
          <div className="h-1.5 mb-3" style={{ background: brandColor }} />
          <div className="flex items-baseline justify-between">
            <span className="font-display font-semibold uppercase text-lg">Total</span>
            <span className="font-mono font-semibold text-xl" style={{ color: brandColor }}>£2,450.00</span>
          </div>
        </div>
        <button data-testid="brand-color-save" onClick={saveColor} disabled={savingColor} className="tm-btn">
          <Save size={16} /> {savingColor ? 'Saving…' : 'Save brand colour'}
        </button>
      </div>
    </div>
  );
}

/* -------------------------- Invoice settings --------------------------- */
function InvoiceSettingsSection({ user, refresh }) {
  const s = user.invoice_settings || {};
  const [prefix, setPrefix] = useState(s.prefix || 'INV-');
  const [nextSeq, setNextSeq] = useState(s.next_seq || '');
  const [remEnabled, setRemEnabled] = useState(!!s.reminders_enabled);
  const [msg, setMsg] = useState(s.reminder_message || DEFAULT_REMINDER_MSG);
  const [everyDays, setEveryDays] = useState(s.reminder_every_days || 7);
  const [maxN, setMaxN] = useState(s.reminder_max || 3);
  const [savingNum, setSavingNum] = useState(false);
  const [savingRem, setSavingRem] = useState(false);

  useEffect(() => {
    const st = user.invoice_settings || {};
    setPrefix(st.prefix || 'INV-');
    setNextSeq(st.next_seq || '');
    setRemEnabled(!!st.reminders_enabled);
    setMsg(st.reminder_message || DEFAULT_REMINDER_MSG);
    setEveryDays(st.reminder_every_days || 7);
    setMaxN(st.reminder_max || 3);
  }, [user]);

  const pad = (n) => String(Math.max(1, parseInt(n) || 1)).padStart(4, '0');
  const preview = `${prefix || 'INV-'}${pad(nextSeq || 1)}`;

  const saveNumbering = async () => {
    setSavingNum(true);
    try {
      await api.patch('/me/invoice-settings', { invoice_prefix: prefix, invoice_next_seq: parseInt(nextSeq) || 1 });
      if (refresh) await refresh();
      toast.success('Invoice numbering saved');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Save failed');
    } finally { setSavingNum(false); }
  };

  const saveReminders = async () => {
    setSavingRem(true);
    try {
      await api.patch('/me/invoice-settings', {
        reminders_enabled: remEnabled, reminder_message: msg,
        reminder_every_days: parseInt(everyDays) || 7, reminder_max: parseInt(maxN) || 3,
      });
      if (refresh) await refresh();
      toast.success('Reminder settings saved');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Save failed');
    } finally { setSavingRem(false); }
  };

  return (
    <div className="space-y-6">
      <div className="tm-card p-6 space-y-4">
        <SectionHead icon={Receipt} title="Invoice Numbering" sub="Match your existing books — set the prefix and the next number to use." />
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="tm-label">Prefix</label>
            <input data-testid="inv-prefix" value={prefix} onChange={(e) => setPrefix(e.target.value)} className="tm-input font-mono" placeholder="INV-" maxLength={12} />
          </div>
          <div>
            <label className="tm-label">Next number</label>
            <input data-testid="inv-next-seq" type="number" min={1} value={nextSeq} onChange={(e) => setNextSeq(e.target.value)} className="tm-input font-mono" placeholder="1" />
          </div>
        </div>
        <div className="border border-slate-200 p-3">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Next invoice will be</p>
          <p className="font-mono font-semibold text-xl mt-1" data-testid="inv-number-preview">{preview}</p>
        </div>
        <button data-testid="inv-numbering-save" onClick={saveNumbering} disabled={savingNum} className="tm-btn"><Save size={16} /> {savingNum ? 'Saving…' : 'Save numbering'}</button>
      </div>

      <div className="tm-card p-6 space-y-4">
        <SectionHead icon={Bell} title="Payment Reminders" sub="Automatically email a polite nudge when an invoice is overdue and still unpaid." />
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <input data-testid="rem-enabled" type="checkbox" checked={remEnabled} onChange={(e) => setRemEnabled(e.target.checked)} className="w-5 h-5 accent-brand" />
          <span className="font-display font-bold uppercase text-sm tracking-wide">Auto-send reminders {remEnabled ? 'ON' : 'OFF'}</span>
        </label>
        <div className={remEnabled ? '' : 'opacity-50 pointer-events-none'}>
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="tm-label">Remind every (days overdue)</label>
              <input data-testid="rem-every" type="number" min={1} max={90} value={everyDays} onChange={(e) => setEveryDays(e.target.value)} className="tm-input" />
            </div>
            <div>
              <label className="tm-label">Max reminders per invoice</label>
              <input data-testid="rem-max" type="number" min={1} max={20} value={maxN} onChange={(e) => setMaxN(e.target.value)} className="tm-input" />
            </div>
          </div>
          <label className="tm-label">Email message</label>
          <textarea data-testid="rem-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={5} className="tm-input" />
          <p className="text-xs text-slate-500 mt-1">Available tags: <code className="font-mono">{'{customer} {invoice_number} {amount} {due_date} {link} {business}'}</code></p>
        </div>
        <button data-testid="rem-save" onClick={saveReminders} disabled={savingRem} className="tm-btn"><Save size={16} /> {savingRem ? 'Saving…' : 'Save reminders'}</button>
      </div>
    </div>
  );
}

/* -------------------------------- Rates -------------------------------- */
function RatesSection() {
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/plans/rates').then((r) => { if (r.data && Object.keys(r.data).length) setRates((p) => ({ ...p, ...r.data })); }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try { await api.put('/plans/rates', rates); toast.success('Rates saved to your account'); }
    catch { toast.error('Could not save rates'); }
    finally { setBusy(false); }
  };

  return (
    <div className="tm-card p-6 space-y-5">
      <SectionHead icon={SlidersHorizontal} title="Material Coverage Rates" sub="Tweak the assumptions the Plan Designer uses to estimate quantities so they match how you work." />
      {RATE_FIELDS.map((grp) => (
        <div key={grp.group}>
          <p className="text-[11px] uppercase tracking-widest text-brand mb-2 font-semibold">{grp.group}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {grp.fields.map((f) => (
              <label key={f.key} className="text-sm">
                {f.label} <span className="text-slate-400">({f.unit})</span>
                <input
                  type="number"
                  step={f.step}
                  value={rates[f.key] ?? DEFAULT_RATES[f.key]}
                  data-testid={`rate-${f.key}`}
                  onChange={(e) => setRates((p) => ({ ...p, [f.key]: Math.max(0, +e.target.value || 0) }))}
                  className="tm-input mt-1"
                />
              </label>
            ))}
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
        <button onClick={() => setRates(DEFAULT_RATES)} data-testid="rates-reset" className="text-xs uppercase text-slate-500 hover:text-slate-900 font-semibold">Reset to defaults</button>
        <button onClick={save} disabled={busy} data-testid="rates-save" className="tm-btn"><Save size={16} /> {busy ? 'Saving…' : 'Save rates'}</button>
      </div>
    </div>
  );
}

/* ----------------------------- Subscription ---------------------------- */
function SubscriptionSection({ user }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get('/billing/status').then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const tierLabel = user.is_pro_plus ? 'Pro+' : user.is_pro ? 'Pro' : 'Free';
  const isPaid = user.is_pro || user.is_pro_plus;
  const statusText = status?.status && status.status !== 'none' ? status.status : null;

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/billing/portal', { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Could not open billing portal');
      setBusy(false);
    }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null);

  return (
    <div className="tm-card p-6 space-y-5">
      <SectionHead icon={CreditCard} title="Subscription" sub="Your current plan and billing." />
      <div className="flex items-center justify-between border border-slate-200 p-4">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Current plan</p>
          <p className="font-display font-semibold text-2xl uppercase tracking-tight">{tierLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`tm-chip ${isPaid ? 'tm-chip-orange' : ''}`} data-testid="subscription-tier-badge">
            {tierLabel}
          </span>
          {statusText && (
            <span className="tm-chip" data-testid="subscription-status">{statusText}</span>
          )}
        </div>
      </div>

        {status?.trial_end && (
        <p className="text-sm text-slate-600 inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-600" /> Free trial ends {fmtDate(status.trial_end)}</p>
      )}
      {status?.current_period_end && (
        <p className="text-sm text-slate-600">Renews {fmtDate(status.current_period_end)}</p>
      )}

      <div className="flex flex-wrap gap-3">
        {isPaid ? (
          <button data-testid="subscription-manage" onClick={openPortal} disabled={busy} className="tm-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ExternalLink size={16} />} Manage subscription
          </button>
        ) : (
          <Link to="/pricing" data-testid="subscription-upgrade" className="tm-btn">
            <Sparkles size={16} /> Upgrade to Pro
          </Link>
        )}
      </div>
      <p className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Billing is handled securely by Stripe. Update your card, invoices or cancel any time from the portal.</p>
    </div>
  );
}

/* ---------------------------- Change Password -------------------------- */
function ChangePassword() {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (next !== confirm) { toast.error('New passwords do not match'); return; }
    setBusy(true);
    try {
      await api.post('/me/password', { current_password: cur, new_password: next });
      toast.success('Password changed');
      setCur(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Could not change password');
    } finally { setBusy(false); }
  };
  return (
    <div className="tm-card p-6 space-y-4" data-testid="change-password-card">
      <SectionHead icon={KeyRound} title="Change Password" sub="Update the password you use to sign in." />
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-sm font-medium">Current password</label>
          <input type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} required data-testid="pwd-current" className="tm-input" placeholder="Your current password" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium">New password</label>
            <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required data-testid="pwd-new" className="tm-input" placeholder="At least 8 characters" />
          </div>
          <div>
            <label className="text-sm font-medium">Confirm new password</label>
            <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="pwd-confirm" className="tm-input" placeholder="Re-type new password" />
          </div>
        </div>
        <button type="submit" disabled={busy || !cur || !next || !confirm} data-testid="pwd-submit" className="tm-btn"><KeyRound size={16} /> {busy ? 'Updating…' : 'Update password'}</button>
      </form>
    </div>
  );
}

/* ------------------------------- Account ------------------------------- */
function AccountSection({ user, logout }) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const isAdmin = user.role === 'admin';

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete('/me', { data: { confirm: deleteConfirm } });
      toast.success('Account deleted. Goodbye 👋');
      try { await logout(); } catch { /* cookie already cleared */ }
      setTimeout(() => { window.location.href = '/'; }, 800);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Could not delete account');
      setDeleting(false);
    }
  };

  return (
    <>
      <div data-testid="settings-danger-zone" className="border border-red-600 bg-red-50 p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-red-600 shrink-0 mt-1" size={28} />
          <div className="flex-1">
            <h2 className="font-display font-semibold text-xl uppercase tracking-tight text-red-700">Danger Zone</h2>
            <p className="text-sm text-slate-700 mt-2">
              Permanently delete your TradesMate account, every quote you&apos;ve saved, your folders and your business details. If you have an active Pro / Pro+ subscription it will be cancelled with Stripe automatically. This cannot be undone.
            </p>
            {isAdmin ? (
              <p className="text-xs uppercase tracking-widest mt-3 text-red-600 font-semibold">Admin accounts cannot self-delete.</p>
            ) : (
              <button
                data-testid="settings-delete-account-open"
                onClick={() => setDeleteOpen(true)}
                className="mt-4 inline-flex items-center gap-2 bg-red-600 text-white border border-red-700 px-4 py-2 font-display font-bold uppercase text-xs tracking-widest hover:bg-red-700"
              >
                <Trash2 size={14} /> Delete my account
              </button>
            )}
          </div>
        </div>
      </div>

      {deleteOpen && (
        <div
          data-testid="delete-account-modal"
          className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) { setDeleteOpen(false); setDeleteConfirm(''); } }}
        >
          <div className="bg-white shadow-md border border-slate-200 w-full max-w-md">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3 bg-red-50">
              <p className="font-display font-semibold uppercase tracking-tight text-lg text-red-700">Delete account?</p>
              <button onClick={() => { setDeleteOpen(false); setDeleteConfirm(''); }} className="p-1 border border-slate-200 bg-white hover:bg-slate-100" aria-label="Close"><X size={14} /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-700 leading-relaxed">
                This will <strong>permanently delete</strong> your account, every saved quote, your folders, your business details and your logo. Any active subscription will be cancelled with Stripe in this same step.
              </p>
              <p className="text-sm text-slate-700">Type your account email <strong>{user.email}</strong> below to confirm.</p>
              <div className="flex items-center gap-2 border border-slate-200 px-2">
                <Mail size={16} className="text-slate-500 shrink-0" />
                <input
                  data-testid="delete-account-confirm-input"
                  autoFocus
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  className="flex-1 outline-none font-mono text-sm py-2"
                  placeholder={user.email}
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button onClick={() => { setDeleteOpen(false); setDeleteConfirm(''); }} className="border border-slate-200 bg-white hover:bg-slate-100 px-4 py-2 text-xs uppercase tracking-widest font-semibold">Cancel</button>
                <button
                  data-testid="delete-account-confirm-btn"
                  onClick={deleteAccount}
                  disabled={deleting || deleteConfirm.trim().toLowerCase() !== (user.email || '').toLowerCase()}
                  className="bg-red-600 text-white border border-red-700 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 text-xs uppercase tracking-widest hover:bg-red-700 font-semibold"
                >
                  {deleting ? 'Deleting…' : 'Yes, delete forever'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
